import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";
import { Box, FormControl, styled, Autocomplete, TextField, Checkbox } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Site from "../../../api/Admin/Site";
import TestFacility from "../../../api/Admin/TestFacility";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import EmailAddressTextField from "../../../components/EmailAddressField";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

const testFacilityValidationSchema = (invalidAbbreviations, invalidTestFacilityNames) => {
  return Yup.object().shape({
    abbreviation: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Abbr. is required')
      .min(3, 'Must be 3 characters')
      .max(3, 'Must be 3 characters')
      .test("unique-name", "Already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidAbbreviations.some((x) => x?.toLowerCase().trim() === lowerCaseValue);
      }),
    testFacilityName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Test Facility Name is required')
      .max(50, 'Must be 50 characters or less')
      .test("unique-name", "Test Facility Name already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidTestFacilityNames.some((name) => name?.toLowerCase().trim() === lowerCaseValue);
      }),
    siteName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Site Name is required')
  });
}

const initialState = {
  testFacilityName: "",
  abbreviation: "",
  isActive: false,
  isExternalTestFacility: false,
  testFacilityFullName: "",
  siteName: "",
  createdByEmail: "",
  createdDate: "",
  primaryContactEmail: ""
};

const TestFacilityManagement = () => {
  const [isNewTestFacility, setIsNewTestFacility] = useState(false);
  const [testFacilityData, setTestFacilityData] = useState(initialState);
  const [testFacilityList, setTestFacilityList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasPrimaryContactEmailError, setPrimaryContactEmailError] = useState(null);

  const [showInactiveFacilities, setShowInactiveFacilities] = useState(false);   

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.AnalyticalSME, Roles.Developer]);

  const initialValues = testFacilityData;
  const invalidAbbreviations = testFacilityList.filter(s => s.abbreviation !== testFacilityData.abbreviation).map(s => s.abbreviation);
  const invalidTestFacilityNames = testFacilityList.filter(s => s.abbreviation !== testFacilityData.abbreviation).map(s => s.testFacilityName);

  const filteredTestFacilities = testFacilityList?.filter(f => showInactiveFacilities || f.isActive) ?? [];

  const resetData = () => setTestFacilityData(initialState);

  useEffect(() => {
    loadAllTestFacilities();
    loadAllSites();
  }, []);

  function loadAllTestFacilities() {
    TestFacility.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ abbreviation: "", testFacilityName: "" });
        array.push(...res);

        setTestFacilityList(array.sort(
          (a,b) => a.abbreviation.localeCompare(b.abbreviation)
        ));
      }
    }); 
  }

  function loadAllSites() {
    Site.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ siteName: "", abbreviation: "" });
        array.push(...res);

        setSiteList(array.sort(
          (a,b) => a.siteName.localeCompare(b.siteName)
        ));
      }
    }); 
  }

  function handleTestFacilitySelection(testFacility) {
    resetData();
    
    if (testFacility?.abbreviation) {
      setIsNewTestFacility(false);
      setTestFacilityData(testFacility);
    }
  }

  function handleAddNewSite() {
    setIsNewTestFacility(true);
    resetData();
  }

  const handleSubmit = async(values) => {
    setIsSaving(true);

    const updatedTestFacilityData = new TestFacility({
      testFacilityName: values.testFacilityName,
      abbreviation: values.abbreviation,
      isActive: values.isActive,
      isExternalTestFacility: values.isExternalTestFacility,
      siteName: values.siteName,
      primaryContactEmail: values.primaryContactEmail
    });

    try {
      const apiAction = isNewTestFacility
        ? TestFacility.add(updatedTestFacilityData)
        : TestFacility.update(testFacilityData.abbreviation, updatedTestFacilityData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'Test Facility saved successfully!', 'Ok');
      setIsNewTestFacility(false);
      loadAllTestFacilities();

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving Test Facility : ${error?.message}`, 'Ok');      
    } finally {
      setIsSaving(false);
    } 
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  function toggleShowInactiveFacilities() {
    const showInactive = !showInactiveFacilities;
    setShowInactiveFacilities(showInactive);

    if (!isNewTestFacility && !showInactive && testFacilityData?.isActive === false) {
      resetData();
    }
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">

      <FormControlLabel sx={{ marginLeft: "5px", marginBottom: "5px"}}
        control={
          <Checkbox
            checked = {showInactiveFacilities}
            onChange={() => toggleShowInactiveFacilities()}
          />}
        label="Show inactive Test Facilities"         
      />

      <StyledBox>
        <FormControl sx={{ m: 1, width: "44rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading Test Facilities..."}
              id="facility-select"
              value={testFacilityData}
              onChange={(e, value) => { handleTestFacilitySelection(value) }}
              disablePortal
              options={filteredTestFacilities ?? []}
              getOptionLabel={(option) => option?.testFacilityFullName ?? ""}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Test Facility"/> }
              autoSelect
            />

            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleAddNewSite}
              >Add New</GlobalSecondaryButton>
            }

          </StyledBox>
        </FormControl>
      </StyledBox>

      {(testFacilityData.abbreviation || isNewTestFacility) && <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={() => testFacilityValidationSchema(invalidAbbreviations, invalidTestFacilityNames)}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched, isValid, dirty }) => (
            <Form style={{margin:"10px", width: "40rem"}}>
    
              <StyledBox>
    
                <TextField style={{ width: "10rem" }}
                  name="abbreviation"
                  label="Abbreviation"
                  variant="outlined" size="small" margin="none"
                  value={values.abbreviation} 
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ maxLength: 3 }} 
                  disabled={!isNewTestFacility}
                  error={touched.abbreviation && Boolean(errors.abbreviation)}
                  helperText={touched.abbreviation && errors.abbreviation}
                />
    
                <Box style={{ justifyContent: "space-between" }}>
                  <FormControlLabel
                    label="Active"
                    style={{marginLeft:"50px"}}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        name="isActive"
                        checked={values.isActive}
                        disabled={!canEdit} 
                      />}
                  />
    
                  <FormControlLabel
                    label="External Facility"
                    style={{marginLeft:"40px"}}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        name="isExternalTestFacility"
                        checked={values.isExternalTestFacility}
                        disabled={!canEdit}  
                      />}
                  />
                </Box>
                
              </StyledBox>
    
              <StyledBox>

                <TextField
                  style={{width: "35rem"}}
                  name="testFacilityName"
                  label="Test Facility name"
                  variant="outlined" size="small" margin="none" 
                  value={values.testFacilityName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!canEdit}
                  error={touched.testFacilityName && Boolean(errors.testFacilityName)}
                  helperText={touched.testFacilityName && errors.testFacilityName}
                />
    
              </StyledBox>
              
              <StyledBox>
                <EmailAddressTextField
                  validatedUserEmail={values.primaryContactEmail}
                  setValidatedUserEmail={(value) => {
                    setFieldValue('primaryContactEmail', value);
                  }}
                  setHasErrors={setPrimaryContactEmailError}
                  isDisabled={!canEdit}
                  isRequired={false}
                  labelText="Primary Contact Email"
                  placeholderText="Primary Contact Email"
                  size={"small"}
                  margin={"none"}
                  fieldWidth={"78%"}
                  showPlusMeButton={true}
                />
              </StyledBox>
    
              <StyledBox style={{marginBottom: "30px"}}>

                <Autocomplete
                  noOptionsText={"Loading Sites..."}
                  value={siteList.find(p => p.siteName === values.siteName) || null}
                  onChange={(e, val) => setFieldValue('siteName', val.siteName) || ''}
                  isOptionEqualToValue={(option, value) => option.siteName === value.siteName}
                  getOptionLabel={(s) => s.isExternalSite ? `EXT - ${s.siteName}` : s.siteName}
                  onBlur={() => setFieldTouched('siteName', true)}
                  disablePortal
                  disabled={!canEdit || siteList.length === 0}
                  options={siteList}
                  sx={{ width: "35rem" }}
                  renderInput={(params) => <TextField 
                    {...params} 
                    name="siteName"  variant="outlined" size="small" margin="none" 
                    label="Site Name"
                    error={touched.siteName && Boolean(errors.siteName)}
                    helperText={touched.siteName && errors.siteName}
                  />}
                  autoSelect                  
                />
    
              </StyledBox>

              <AuditInfoLabel auditObject={values} />            

              { canEdit &&
                <StyledBox style={{ marginTop: "30px", marginBottom: "20px" }}>
                  <GlobalButton sx={{ width: "8rem" }}
                    variant='contained'             
                    type="submit"
                    disabled={!dirty || isSaving || !!hasPrimaryContactEmailError}
                  >
                    Save
                  </GlobalButton>
                </StyledBox>
              }
    
            </Form>
          )}
        </Formik>
      } 

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

    </StyledBox>
  );
};

export default TestFacilityManagement;
